import { useEffect } from "react"
import Sticky from "react-stickynode"
import { useTranslation } from "next-i18next"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import dynamic from "next/dynamic"
import MetaData from "@/common/components/MetaData"
import SectionLine from "@/common/components/_UI/SectionLine"

import MasterUserData from "@/common/assets/image/solutions/defi/MasterUserData.webp"
import Swiper0 from "@/common/assets/image/solutions/defi/Swiper0.png"
import Swiper1 from "@/common/assets/image/solutions/defi/Swiper1.webp"
import Swiper2 from "@/common/assets/image/solutions/defi/Swiper2.png"
import Swiper3 from "@/common/assets/image/solutions/defi/Swiper3.webp"
import SubDesc1 from "@/common/assets/image/solutions/defi/SubDesc1.svg"
import SubDesc2 from "@/common/assets/image/solutions/defi/SubDesc2.svg"
import SubDesc3 from "@/common/assets/image/solutions/defi/SubDesc3.svg"
import SubDesc4 from "@/common/assets/image/solutions/defi/SubDesc4.svg"
import TAGProfile from "@/common/assets/image/homepage/TAGJoe.png"
import TAGBanner from "@/common/assets/image/solutions/defi/TAGBanner.webp"

const NavbarNew = dynamic(import("@/containers/common/NavbarNew"))

import { DarkBGWrapper, LayoutWrapper } from "@/common/components/LayoutStyle"
import Description from "@/common/components/Description"
import { ProductBanner } from "@/common/components/ProductBanner"
import {
  ProductNames,
  SolutionNames,
  SolutionPlans
} from "@/common/data/Products"
import Footer from "@/containers/common/FooterNew"
import NextImg from "@/common/components/NextImg"
import { PricingBoard } from "@/common/components/Pricing"
import { useRouter } from "next/router"
import DataSwiper from "@/common/components/DataSwiper"
import { SuccessStory } from "@/common/components/SuccessStory"
import ContactUsSection from "@/common/components/ContactUs/ContactUsSection"

const DefiPage = () => {
  const { t } = useTranslation(["defi"])
  const router = useRouter()

  useEffect(() => {
    const { plan } = router.query
    if (plan != null) {
      setTimeout(() => {
        document.getElementById("contactUsSection")?.scrollIntoView({
          behavior: "smooth",
          block: "start"
        })
      }, 200)
    }
  })

  return (
    <>
      <MetaData
        title={t("meta.title")}
        description={t("meta.description")}
        url={t("meta.url")}
        image="https://storage.googleapis.com/kryptogo-official-website-public/og-logo.webp"
        languageAlternates={[
          {
            hrefLang: "en-US",
            href: "https://www.kryptogo.com/solutions/defi"
          },
          {
            hrefLang: "zh-TW",
            href: "https://www.kryptogo.com/tw/solutions/defi"
          },
          {
            hrefLang: "zh-CN",
            href: "https://www.kryptogo.com/cn/solutions/defi"
          },
          {
            hrefLang: "ja-JP",
            href: "https://www.kryptogo.com/ja/solutions/defi"
          }
        ]}
      />

      <Sticky top={0} innerZ={1000} activeClass="sticky-active">
        <NavbarNew
          contactOnClick={e => {
            e.preventDefault()
            document.getElementById("contactUsSection")?.scrollIntoView({
              behavior: "smooth",
              block: "start"
            })
          }}
        />
      </Sticky>
      <DarkBGWrapper></DarkBGWrapper>
      <LayoutWrapper>
        <ProductBanner
          bannerType={SolutionNames.Defi}
          title={t("Banner.productName")}
          titleh1={t("Banner.title")}
          description={t("Banner.description")}
          primaryButton={{
            content: t("Banner.button1"),
            onClick: (e: any) => {
              e.preventDefault()
              document.getElementById("DeFiDesc1")?.scrollIntoView({
                behavior: "smooth",
                block: "start"
              })
            }
          }}
          subtleButton={{
            content: t("Banner.button2"),
            onClick: (e: any) => {
              e.preventDefault()
              document.getElementById("contactUsSection")?.scrollIntoView({
                behavior: "smooth",
                block: "start"
              })
            }
          }}
        />
        <SectionLine style={{ marginBottom: "-60px" }} />
        <Description
          id="DeFiDesc1"
          backgroundType="envelope"
          items={[
            {
              titles: [{ text: t("Desc.1.title"), textType: "h3" }],
              checkList: [
                {
                  title: t("Desc.1.checkList.1.title"),
                  list: [
                    { description: t("Desc.1.checkList.1.item.1") },
                    { description: t("Desc.1.checkList.1.item.2") },
                    { description: t("Desc.1.checkList.1.item.3") }
                  ]
                },
                {
                  title: t("Desc.1.checkList.2.title"),
                  list: [
                    { description: t("Desc.1.checkList.2.item.1") },
                    { description: t("Desc.1.checkList.2.item.2") },
                    { description: t("Desc.1.checkList.2.item.3") }
                  ]
                },
                {
                  title: t("Desc.1.checkList.3.title"),
                  list: [
                    { description: t("Desc.1.checkList.3.item.1") },
                    { description: t("Desc.1.checkList.3.item.2") },
                    { description: t("Desc.1.checkList.3.item.3") }
                  ]
                }
              ],
              wide: true
            }
          ]}
        />
        <Description
          backgroundType="none"
          items={[
            {
              titles: [{ text: t("Desc.2.title"), textType: "h3" }],
              content: t("Desc.2.description"),
              wide: true
            }
          ]}
        />
        <NextImg
          className="kg-wrapper"
          src={MasterUserData.src}
          width={75}
          alt="Master User Data"
        />
        <Description
          backgroundType="none"
          items={[
            {
              titles: [{ text: t("Desc.3.title"), textType: "h3" }],
              content: t("Desc.3.description"),
              subDescriptions: [
                {
                  icon: SubDesc1,
                  title: t("Desc.3.subDesc.0.title"),
                  content: t("Desc.3.subDesc.0.description")
                },
                {
                  icon: SubDesc2,
                  title: t("Desc.3.subDesc.1.title"),
                  content: t("Desc.3.subDesc.1.description")
                },
                {
                  icon: SubDesc3,
                  title: t("Desc.3.subDesc.2.title"),
                  content: t("Desc.3.subDesc.2.description")
                },
                {
                  icon: SubDesc4,
                  title: t("Desc.3.subDesc.3.title"),
                  content: t("Desc.3.subDesc.3.description")
                }
              ],
              wide: true
            }
          ]}
        />
        <DataSwiper
          style={{ paddingTop: "10vh" }}
          items={[
            {
              titleh3: t("swiper.items.0.title"),
              titleh3Type: "normal",
              description: t("swiper.items.0.description"),
              comp2: <NextImg src={Swiper0.src} />,
              checkList: [
                {
                  list: [
                    {
                      title: t("swiper.items.0.checkList.0.title"),
                      description: t("swiper.items.0.checkList.0.description")
                    },
                    {
                      title: t("swiper.items.0.checkList.1.title"),
                      description: t("swiper.items.0.checkList.1.description")
                    },
                    {
                      title: t("swiper.items.0.checkList.2.title"),
                      description: t("swiper.items.0.checkList.2.description")
                    },
                    {
                      title: t("swiper.items.0.checkList.3.title"),
                      description: t("swiper.items.0.checkList.3.description")
                    }
                  ]
                }
              ]
            },
            {
              titleh3: t("swiper.items.2.title"),
              titleh3Type: "normal",
              description: t("swiper.items.2.description"),
              comp2: <NextImg src={Swiper2.src} />,
              checkList: [
                {
                  list: [
                    {
                      title: t("swiper.items.2.checkList.0.title"),
                      description: t("swiper.items.2.checkList.0.description")
                    },
                    {
                      title: t("swiper.items.2.checkList.1.title"),
                      description: t("swiper.items.2.checkList.1.description")
                    },
                    {
                      title: t("swiper.items.2.checkList.2.title"),
                      description: t("swiper.items.2.checkList.2.description")
                    }
                  ]
                }
              ]
            },
            {
              titleh3: t("swiper.items.1.title"),
              titleh3Type: "normal",
              description: t("swiper.items.1.description"),
              comp2: <NextImg src={Swiper1.src} />,
              checkList: [
                {
                  list: [
                    {
                      title: t("swiper.items.1.checkList.0.title"),
                      description: t("swiper.items.1.checkList.0.description")
                    },
                    {
                      title: t("swiper.items.1.checkList.1.title"),
                      description: t("swiper.items.1.checkList.1.description")
                    },
                    {
                      title: t("swiper.items.1.checkList.2.title"),
                      description: t("swiper.items.1.checkList.2.description")
                    },
                    {
                      title: t("swiper.items.1.checkList.3.title"),
                      description: t("swiper.items.1.checkList.3.description")
                    }
                  ]
                }
              ]
            },
            {
              titleh3: t("swiper.items.3.title"),
              titleh3Type: "normal",
              description: t("swiper.items.3.description"),
              comp2: <NextImg src={Swiper3.src} />,
              checkList: [
                {
                  list: [
                    {
                      title: t("swiper.items.3.checkList.0.title"),
                      description: t("swiper.items.3.checkList.0.description")
                    },
                    {
                      title: t("swiper.items.3.checkList.1.title"),
                      description: t("swiper.items.3.checkList.1.description")
                    },
                    {
                      title: t("swiper.items.3.checkList.2.title"),
                      description: t("swiper.items.3.checkList.2.description")
                    }
                  ]
                }
              ]
            }
          ]}
        />
        <SuccessStory
          title={t("Success.title")}
          slogan={t("Success.slogan")}
          items={[
            {
              titleh3: t("Success.story.0.title"),
              titleh3Type: "normal",
              description: t("Success.story.0.description"),
              checkList: [
                {
                  listColor: "disabled",
                  list: [{ description: t("Success.story.0.checkList.0") }]
                }
              ],
              primaryButton: {
                content: t("Success.story.button"),
                href: "/use-case/tag-wallet"
              },
              customerCard: {
                styleType: "bottom",
                company: t("Success.story.customer.name"),
                bgImage: TAGBanner.src,
                description: t("Success.story.customer.quote"),
                image: TAGProfile
              }
            }
          ]}
        />
        <SectionLine />
        <PricingBoard
          id="Pricing"
          description={t("Pricing.description")}
          slogan={t("Pricing.slogan")}
          cards={[
            {
              isRecommended: true,
              title: t("Pricing.card.1.title"),
              description: t("Pricing.card.1.description"),
              button: {
                id: "defi-pro-plan-choose-button",
                text: t("Pricing.card.1.button"),
                onClick: (e: any) => {
                  e.preventDefault()
                  router.push(
                    { query: { plan: SolutionPlans.DefiPro } },
                    undefined,
                    { shallow: true }
                  )
                }
              },
              productLinks: [
                ProductNames.User360,
                ProductNames.WalletBuilder,
                ProductNames.OAuthSDK,
                ProductNames.WalletAPI,
                ProductNames.WalletSDK,
                ProductNames.WalletExtension,
                ProductNames.CompliancePro,
                ProductNames.ComplianceAPI
              ],
              detail: [
                {
                  title: t("Pricing.card.1.item.1.title"),
                  list: {
                    listColor: "disabled",
                    list: [
                      {
                        title: t("Pricing.card.1.item.1.list.1.title"),
                        description: t("Pricing.card.1.item.1.list.1.content")
                      },
                      {
                        title: t("Pricing.card.1.item.1.list.2.title"),
                        description: t("Pricing.card.1.item.1.list.2.content")
                      }
                    ]
                  }
                },
                {
                  title: t("Pricing.card.1.item.2.title"),
                  list: {
                    listColor: "disabled",
                    list: [
                      {
                        title: t("Pricing.card.1.item.2.list.1.title"),
                        description: t("Pricing.card.1.item.2.list.1.content")
                      },
                      {
                        title: t("Pricing.card.1.item.2.list.2.title"),
                        description: t("Pricing.card.1.item.2.list.2.content")
                      }
                    ]
                  }
                },
                {
                  title: t("Pricing.card.1.item.3.title"),
                  list: {
                    listColor: "disabled",
                    list: [
                      {
                        noIcon: true,
                        description: t("Pricing.card.1.item.3.list.1.content")
                      }
                    ]
                  }
                },
                {
                  title: t("Pricing.card.1.item.4.title"),
                  list: {
                    listColor: "disabled",
                    list: [
                      {
                        title: t("Pricing.card.1.item.4.list.1.title"),
                        description: t("Pricing.card.1.item.4.list.1.content")
                      },
                      {
                        title: t("Pricing.card.1.item.4.list.2.title"),
                        description: t("Pricing.card.1.item.4.list.2.content")
                      },
                      {
                        title: t("Pricing.card.1.item.4.list.3.title"),
                        description: t("Pricing.card.1.item.4.list.3.content")
                      },
                      {
                        title: t("Pricing.card.1.item.4.list.4.title"),
                        description: t("Pricing.card.1.item.4.list.4.content")
                      }
                    ]
                  }
                },
                {
                  title: t("Pricing.card.1.item.5.title"),
                  list: {
                    listColor: "disabled",
                    list: [
                      {
                        title: t("Pricing.card.1.item.5.list.1.title"),
                        description: t("Pricing.card.1.item.5.list.1.content")
                      },
                      {
                        title: t("Pricing.card.1.item.5.list.2.title"),
                        description: t("Pricing.card.1.item.5.list.2.content")
                      },
                      {
                        title: t("Pricing.card.1.item.5.list.3.title"),
                        description: t("Pricing.card.1.item.5.list.3.content")
                      },
                      {
                        title: t("Pricing.card.1.item.5.list.4.title"),
                        description: t("Pricing.card.1.item.5.list.4.content")
                      }
                    ]
                  }
                }
              ]
            },

            {
              title: t("Pricing.card.2.title"),
              description: t("Pricing.card.2.description"),
              button: {
                id: "defi-lite-plan-choose-button",
                text: t("Pricing.card.2.button"),
                type: "subtle",
                onClick: (e: any) => {
                  e.preventDefault()
                  router.push(
                    { query: { plan: SolutionPlans.DefiLite } },
                    undefined,
                    { shallow: true }
                  )
                }
              },
              productLinks: [
                ProductNames.User360,
                ProductNames.OAuthSDK,
                ProductNames.WalletBuilder,
                ProductNames.NFTAirdrop,
                ProductNames.ComplyFlow,
                ProductNames.ComplianceLite
              ],
              detail: [
                {
                  title: t("Pricing.card.2.item.1.title"),
                  list: {
                    listColor: "disabled",
                    list: [
                      {
                        title: t("Pricing.card.2.item.1.list.1.title"),
                        description: t("Pricing.card.2.item.1.list.1.content")
                      }
                    ]
                  }
                },
                {
                  title: t("Pricing.card.2.item.2.title"),
                  list: {
                    listColor: "disabled",
                    list: [
                      {
                        title: t("Pricing.card.2.item.2.list.1.title"),
                        description: t("Pricing.card.2.item.2.list.1.content")
                      }
                    ]
                  }
                },
                {
                  title: t("Pricing.card.2.item.3.title"),
                  list: {
                    listColor: "disabled",
                    list: [
                      {
                        noIcon: true,
                        description: t("Pricing.card.2.item.3.list.1.content")
                      }
                    ]
                  }
                },
                {
                  title: t("Pricing.card.2.item.4.title"),
                  list: {
                    listColor: "disabled",
                    list: [
                      {
                        title: t("Pricing.card.2.item.4.list.1.title"),
                        description: t("Pricing.card.2.item.4.list.1.content")
                      },
                      {
                        title: t("Pricing.card.2.item.4.list.2.title"),
                        description: t("Pricing.card.2.item.4.list.2.content")
                      }
                    ]
                  }
                },
                {
                  title: t("Pricing.card.2.item.5.title"),
                  list: {
                    listColor: "disabled",
                    list: [
                      {
                        title: t("Pricing.card.2.item.5.list.1.title"),
                        description: t("Pricing.card.2.item.5.list.1.content")
                      }
                    ]
                  }
                }
              ]
            }
          ]}
        />
        <ContactUsSection
          page={SolutionNames.Defi}
          description={t("ContactUs.description")}
          slogan={t("ContactUs.slogan")}
          body={t("ContactUs.body")}
          plans={[SolutionPlans.DefiPro, SolutionPlans.DefiLite]}
        />
        <Footer />
      </LayoutWrapper>
    </>
  )
}

export const getStaticProps = async ({ locale }: { locale: string }) => ({
  props: {
    ...(await serverSideTranslations(locale, ["common", "contactUs", "defi"]))
  }
})

export default DefiPage
